/**
 * Introduction Service
 * Manages introduction requests via Pharmunity platform
 */

import api from './api';
import { supabase } from '../lib/supabase';

const API_BASE = '/introductions';

export const introductionService = {
  /**
   * Create a new introduction request via backend API
   * @param {Object} params - Request parameters
   * @returns {Promise<Object>} - Created request
   */
  async createRequest({
    sellerId,
    buyerId,
    requestType = 'seller_to_buyer',
    requestMessage,
    assetId = null
  }) {
    try {
      const response = await api.post(API_BASE, {
        sellerId,
        buyerId,
        requestType,
        requestMessage,
        assetId
      });
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create introduction request');
    }
  },

  /**
   * Get introduction requests for a seller
   * @param {string} sellerId - Seller UUID
   * @param {Object} [options] - Query options
   * @returns {Promise<Array>}
   */
  async getSellerRequests(sellerId, options = {}) {
    const { status = null, limit = 50 } = options;

    let query = supabase
      .from('introduction_requests')
      .select(`
        *,
        buyers:buyer_id (
          id,
          company_name,
          contact_name
        )
      `)
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get introduction requests for a connector
   * @param {string} connectorUserId - Connector user UUID
   * @param {Object} [options] - Query options
   * @returns {Promise<Array>}
   */
  async getConnectorRequests(connectorUserId, options = {}) {
    const { status = null, limit = 50 } = options;

    let query = supabase
      .from('introduction_requests')
      .select(`
        *,
        sellers:seller_id (
          id,
          company_name,
          company_name_cn
        ),
        buyers:buyer_id (
          id,
          company_name,
          contact_name
        )
      `)
      .eq('connector_user_id', connectorUserId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Respond to an introduction request (as connector)
   * @param {string} requestId - Request UUID
   * @param {string} status - 'accepted' or 'declined'
   * @param {string} [response] - Optional response message
   * @returns {Promise<Object>}
   */
  async respondToRequest(requestId, status, response = null) {
    const updateData = {
      status,
      connector_response: response,
      connector_responded_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('introduction_requests')
      .update(updateData)
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Mark introduction as made (connector confirmed they made the intro)
   * @param {string} requestId - Request UUID
   * @returns {Promise<Object>}
   */
  async markIntroductionMade(requestId) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .update({
        introduction_made: true,
        introduction_date: new Date().toISOString(),
        status: 'completed',
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Link a conversation to an introduction request
   * @param {string} requestId - Request UUID
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Object>}
   */
  async linkConversation(requestId, conversationId) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .update({
        conversation_started: true,
        conversation_id: conversationId,
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get a single introduction request by ID
   * @param {string} requestId - Request UUID
   * @returns {Promise<Object>}
   */
  async getRequest(requestId) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select(`
        *,
        sellers:seller_id (
          id,
          company_name,
          company_name_cn
        ),
        buyers:buyer_id (
          id,
          company_name,
          contact_name
        )
      `)
      .eq('id', requestId)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Check if there's an existing pending request
   * @param {string} sellerId - Seller UUID
   * @param {string} buyerId - Buyer UUID
   * @returns {Promise<Object|null>}
   */
  async checkExistingRequest(sellerId, buyerId) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select('*')
      .eq('seller_id', sellerId)
      .eq('buyer_id', buyerId)
      .in('status', ['pending', 'accepted'])
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Cancel a pending introduction request
   * @param {string} requestId - Request UUID
   * @returns {Promise<void>}
   */
  async cancelRequest(requestId) {
    const { error } = await supabase
      .from('introduction_requests')
      .update({
        status: 'expired',
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .eq('status', 'pending'); // Only cancel pending requests

    if (error) throw error;
  },

  /**
   * Get introduction statistics for a seller
   * @param {string} sellerId - Seller UUID
   * @returns {Promise<Object>}
   */
  async getSellerStats(sellerId) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select('status')
      .eq('seller_id', sellerId);

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      pending: 0,
      accepted: 0,
      declined: 0,
      completed: 0,
      expired: 0,
      successRate: 0
    };

    data?.forEach(req => {
      if (stats.hasOwnProperty(req.status)) {
        stats[req.status]++;
      }
    });

    // Calculate success rate (completed / (accepted + declined + completed))
    const responded = stats.accepted + stats.declined + stats.completed;
    if (responded > 0) {
      stats.successRate = Math.round((stats.completed / responded) * 100);
    }

    return stats;
  },

  // ============================================
  // Admin API Methods
  // ============================================

  /**
   * Get all introduction requests (admin only)
   * @param {Object} [options] - Query options
   * @returns {Promise<Object>}
   */
  async getAllRequests(options = {}) {
    const { status, limit = 50, offset = 0 } = options;
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await api.get(`${API_BASE}?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch introduction requests');
    }
  },

  /**
   * Get introduction request statistics (admin only)
   * @returns {Promise<Object>}
   */
  async getStats() {
    try {
      const response = await api.get(`${API_BASE}/stats`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch statistics');
    }
  },

  /**
   * Accept an introduction request (admin only)
   * @param {string} requestId - Request UUID
   * @param {string} [adminMessage] - Optional admin message
   * @returns {Promise<Object>}
   */
  async acceptRequest(requestId, adminMessage = null) {
    try {
      const response = await api.put(`${API_BASE}/${requestId}/accept`, {
        adminMessage
      });
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to accept request');
    }
  },

  /**
   * Decline an introduction request (admin only)
   * @param {string} requestId - Request UUID
   * @param {string} [adminMessage] - Optional admin message
   * @returns {Promise<Object>}
   */
  async declineRequest(requestId, adminMessage = null) {
    try {
      const response = await api.put(`${API_BASE}/${requestId}/decline`, {
        adminMessage
      });
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to decline request');
    }
  },

  /**
   * Complete an introduction request (admin only)
   * @param {string} requestId - Request UUID
   * @param {Object} [options] - Completion options
   * @returns {Promise<Object>}
   */
  async completeRequest(requestId, options = {}) {
    const { adminMessage, targetContactName, targetContactEmail } = options;
    try {
      const response = await api.put(`${API_BASE}/${requestId}/complete`, {
        adminMessage,
        targetContactName,
        targetContactEmail
      });
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to complete request');
    }
  }
};

export default introductionService;
